import React, { createContext, useContext, useEffect, useState } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TENANT_KEY = "wa_tenant_slug";

/**
 * Resolves the active tenant slug. Priority:
 *   1. URL query param `?tenant=<slug>` (e.g. bo-tito.com/?tenant=acme)
 *   2. URL path prefix `/t/<slug>` (e.g. bo-tito.com/t/acme/login)
 *   3. sessionStorage (sticky during the session after first detection)
 *   4. Subdomain (e.g. acme.bo-tito.com — only if wildcard DNS is configured)
 * Returns "" if no tenant is active.
 */
export function detectSubdomain() {
  // 1. Query param
  const url = new URL(window.location.href);
  const fromQuery = (url.searchParams.get("tenant") || "").trim().toLowerCase();
  if (fromQuery) {
    try { sessionStorage.setItem(TENANT_KEY, fromQuery); } catch { /* ignore */ }
    return fromQuery;
  }
  // 2. Path prefix /t/<slug>
  const pathMatch = window.location.pathname.match(/^\/t\/([a-z0-9][a-z0-9-]{1,30}[a-z0-9])(?:\/|$)/i);
  if (pathMatch) {
    const slug = pathMatch[1].toLowerCase();
    try { sessionStorage.setItem(TENANT_KEY, slug); } catch { /* ignore */ }
    return slug;
  }
  // 3. SessionStorage (sticky)
  try {
    const stored = (sessionStorage.getItem(TENANT_KEY) || "").trim().toLowerCase();
    if (stored) return stored;
  } catch { /* ignore */ }
  // 4. Subdomain (wildcard DNS)
  const host = window.location.hostname;
  if (!host || host === "localhost" || /^\d+\.\d+\.\d+\.\d+$/.test(host)) return "";
  const parts = host.split(".");
  if (parts.length < 3) return "";
  const first = parts[0].toLowerCase();
  if (["www", "admin", "api", "app"].includes(first)) return "";
  return first;
}

export function clearTenant() {
  try { sessionStorage.removeItem(TENANT_KEY); } catch { /* ignore */ }
}

const TenantContext = createContext({ tenant: null, loading: true, refresh: () => {} });

export function TenantProvider({ children }) {
  const [state, setState] = useState({ tenant: null, loading: true });

  const loadTenant = () => {
    const sub = detectSubdomain();
    if (!sub) {
      setState({ tenant: null, loading: false });
      return;
    }
    axios
      .get(`${API}/public/tenant`, { params: { subdomain: sub } })
      .then(({ data }) => {
        if (data && data.found) {
          setState({ tenant: { subdomain: sub, ...data }, loading: false });
          const accent = data.branding?.accent_color;
          if (accent) document.documentElement.style.setProperty("--tenant-accent", accent);
          const bn = data.branding?.brand_name;
          if (bn) document.title = bn;
        } else {
          // Tenant not found → clear stale session and unset
          clearTenant();
          setState({ tenant: null, loading: false });
        }
      })
      .catch(() => setState({ tenant: null, loading: false }));
  };

  useEffect(() => { loadTenant(); /* eslint-disable-next-line */ }, []);

  return (
    <TenantContext.Provider value={{ ...state, refresh: loadTenant }}>
      {children}
    </TenantContext.Provider>
  );
}

export function useTenant() {
  return useContext(TenantContext);
}
